import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import {
  getLibraryLessons,
  permanentDeleteLibraryLesson,
  restoreLibraryLesson,
  type TeacherLessonRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { LessonIcon } from "./LessonIcon";

export default function TeacherDeletedLessonsPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [lessons, setLessons] = useState<TeacherLessonRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getLibraryLessons(bearer, { limit: 200, deleted: true });
      setLessons(data.lessons);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load deleted lessons");
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleRestore(lesson: TeacherLessonRecord) {
    if (!bearer || busyId) return;
    setBusyId(lesson.id);
    setError(null);
    try {
      await restoreLibraryLesson(bearer, lesson.id);
      setLessons((rows) => rows.filter((r) => r.id !== lesson.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setBusyId(null);
    }
  }

  async function handlePermanentDelete(lesson: TeacherLessonRecord) {
    if (!bearer || busyId) return;
    if (
      !window.confirm(
        `Permanently delete “${lesson.title}”? This cannot be undone. Lessons with student cohort work cannot be permanently deleted.`
      )
    ) {
      return;
    }
    setBusyId(lesson.id);
    setError(null);
    try {
      await permanentDeleteLibraryLesson(bearer, lesson.id);
      setLessons((rows) => rows.filter((r) => r.id !== lesson.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Permanent delete failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="teacher-page">
      <header className="teacher-page-header">
        <div>
          <p className="teacher-page-eyebrow">
            <Link to="/teacher/lessons">← My lessons</Link>
          </p>
          <h1 className="teacher-page-title">Recently deleted lessons</h1>
          <p className="teacher-page-lede muted">
            Lessons here are hidden from your library and courses. Restore them or permanently delete when
            you no longer need them.
          </p>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : lessons.length === 0 ? (
        <p className="muted">No recently deleted lessons.</p>
      ) : (
        <div className="teacher-table-wrap">
          <table className="teacher-table teacher-lessons-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Deleted</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lessons.map((l) => (
                <tr key={l.id}>
                  <td>
                    <span className="teacher-lesson-row-title">
                      <LessonIcon
                        lessonId={l.id}
                        title={l.title}
                        iconName={l.iconName}
                        bgColor={l.iconBgColor}
                        fgColor={l.iconFgColor}
                        workspaceMode={l.workspaceMode}
                        size={2.25}
                      />
                      <strong>{l.title}</strong>
                    </span>
                  </td>
                  <td className="muted small">
                    {l.deletedAt ? new Date(l.deletedAt).toLocaleString() : "-"}
                  </td>
                  <td className="teacher-table-actions teacher-lesson-row-actions">
                    <button
                      type="button"
                      className="btn secondary tight teacher-icon-btn"
                      title="Restore"
                      aria-label={`Restore ${l.title}`}
                      disabled={busyId === l.id}
                      onClick={() => void handleRestore(l)}
                    >
                      <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                    <button
                      type="button"
                      className="btn secondary danger tight teacher-icon-btn"
                      title="Delete permanently"
                      aria-label={`Permanently delete ${l.title}`}
                      disabled={busyId === l.id}
                      onClick={() => void handlePermanentDelete(l)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
